/* AX9 cost & time calculator.
   Calculation logic only: prices and bottle counts come from the Shopify products
   rendered into the <select>; labels, defaults and assumptions come from the
   section settings (JSON config). Never forces a savings claim. */
(() => {
  'use strict';

  function init(root) {
    if (root.dataset.ax9CalcReady) return;
    root.dataset.ax9CalcReady = '1';

    const cfg = JSON.parse(root.querySelector('[data-ax9-calc-config]').textContent);
    const t = cfg.t;
    const form = root.querySelector('form');
    const statsEl = root.querySelector('[data-ax9-calc-stats]');
    const barsEl = root.querySelector('[data-ax9-calc-bars]');
    const savEl = root.querySelector('[data-ax9-calc-savings]');
    const assumeEl = root.querySelector('[data-ax9-calc-assume]');

    const locale = cfg.locale || document.documentElement.lang || 'es';
    const moneyFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: cfg.currency || 'EUR', maximumFractionDigits: 2 });
    const money = (n) => moneyFmt.format(Math.round(n * 100) / 100);
    const num = (name) => {
      const v = parseFloat(form.elements[name]?.value);
      return Number.isFinite(v) && v >= 0 ? v : 0;
    };
    const time = (m) => {
      if (m < 60) return `${Math.round(m)} ${t.min}`;
      return `${(Math.round(m / 6) / 10).toLocaleString(locale)} ${t.hour}`;
    };
    const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const fill = (tpl, map) => esc(tpl || '').replace(/\[([a-z_]+)\]/g, (m, k) => (k in map ? esc(map[k]) : m));

    function stat(title, rows, cls) {
      return `<div class="ax9-stat ax9-stat--${cls}"><div class="ax9-stat__l">${esc(title)}</div><dl>${rows
        .map(([k, v]) => `<div class="ax9-stat__row"><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`)
        .join('')}</dl></div>`;
    }
    function bars(title, tv, av, fmt) {
      const max = Math.max(tv, av, 1);
      const row = (k, cls, v) =>
        `<div class="ax9-bar"><span class="ax9-bar__k">${esc(k)}</span><span class="ax9-bar__track"><span class="ax9-bar__fill ax9-bar__fill--${cls}" style="width:${(v / max) * 100}%"></span></span><span class="ax9-bar__v">${esc(fmt(v))}</span></div>`;
      return `<div><div class="ax9-bars__title">${esc(title)}</div>${row(t.barTrad, 'trad', tv)}${row(t.barAx9, 'ax9', av)}</div>`;
    }

    function recalc() {
      const wash = num('wash'), freq = num('freq'), mins = num('mins'), extra = num('extra'), other = num('other');
      const uses = num('uses'), care = num('care'), amin = num('amin');
      const opt = form.elements.pack?.selectedOptions?.[0];
      const pack = opt ? { title: opt.dataset.title, price: parseInt(opt.dataset.price, 10) / 100, bottles: parseInt(opt.dataset.bottles, 10) } : null;

      // Traditional method
      const tMonthC = freq * (wash + extra) + other;
      const tYearC = tMonthC * 12;
      const tMonthT = freq * mins;
      const tYearT = tMonthT * 12;

      // AX9 (estimates)
      let aYearC = null;
      const aYearT = care * amin * 12;
      const rows = [];
      if (pack && pack.bottles > 0) {
        const cpa = uses > 0 ? pack.price / (pack.bottles * uses) : null;
        aYearC = cpa !== null ? care * cpa * 12 : null;
        const appMin = pack.bottles * cfg.usesMin;
        const appMax = pack.bottles * cfg.usesMax;
        const months = care > 0 && uses > 0 ? (pack.bottles * uses) / care : null;
        rows.push([t.packCost, money(pack.price)]);
        rows.push([t.bottles, String(pack.bottles)]);
        rows.push([t.apps, `${appMin}–${appMax}`]);
        rows.push([t.costPerApp, `${money(pack.price / appMax)}–${money(pack.price / appMin)}`]);
        rows.push([t.duration, months !== null ? `${Math.round(months)} ${t.months}` : '—']);
      }

      statsEl.innerHTML =
        stat(t.tradTitle, [[t.monthlyCost, money(tMonthC)], [t.yearlyCost, money(tYearC)], [t.monthlyTime, time(tMonthT)], [t.yearlyTime, time(tYearT)]], 'trad') +
        (pack ? stat(`${t.ax9Prefix} ${pack.title}`, rows, 'ax9') : '');

      barsEl.innerHTML = pack ? bars(t.barsMoney, tYearC, aYearC ?? 0, money) + bars(t.barsTime, tYearT, aYearT, time) : '';

      savEl.hidden = !pack;
      if (pack) {
        if (aYearC !== null && aYearC < tYearC) {
          savEl.classList.remove('ax9-savings--neutral');
          savEl.innerHTML = `<span class="ax9-savings__l">${esc(t.savings)}</span><span class="ax9-savings__v">${esc(money(tYearC - aYearC))}</span>`;
        } else {
          savEl.classList.add('ax9-savings--neutral');
          savEl.innerHTML = `<span class="ax9-savings__l">${esc(t.neutral)}</span><span class="ax9-savings__v">${esc(t.neutralText)}</span>`;
        }
      }

      const map = {
        lavado: money(wash), extras: money(extra), otros: money(other), lavados: freq, minutos: mins,
        pack: pack ? pack.title : '—', botellas: pack ? pack.bottles : '—', precio: pack ? money(pack.price) : '—',
        usos: uses, rango: `${cfg.usesMin}–${cfg.usesMax}`, veces: care, minutos_ax9: amin,
      };
      assumeEl.innerHTML = [t.aWash, t.aFreq, t.aPack, t.aUses, t.aCare].filter(Boolean).map((tpl) => `<li>${fill(tpl, map)}</li>`).join('');
    }

    form.addEventListener('input', recalc);
    form.addEventListener('change', recalc);
    recalc();
  }

  const boot = () => document.querySelectorAll('[data-ax9-calc]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
